import {
  Box,
  styled,
  IconButton,
  InputAdornment,
  Grid,
  Button,
  Typography,
  FormHelperText as MuiFormHelperText,
  TextField,
  Dialog as MuiDialog,
} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import {getCookie} from 'cookies-next';
import {useEffect, useRef, useState} from 'react';
import {mediaService} from '@jobseeker/common-core/services';
import Loader from '@jobseeker/lotus/components/Loader';
import {useFormik} from 'formik';
import {
  TrackEventUBA,
  getUbaPageName,
  trackFieldValueLevel,
} from '@jobseeker/common-core/utils';
import {
  HiristBackground,
  PhoneIcon,
  indianFLag,
} from '@jobseeker/lotus/assets/images';
import {LockHirist, UnlockHirist} from '@jobseeker/lotus/assets/icons';
import {
  ChevronRight,
  CrossIcon,
  RightArrow,
  OrangeRightArrow,
} from '@jobseeker/lotus/assets/icons';
import {surveyService} from '@jobseeker/common-core/services';
import {
  validationSchemaLogin,
  validationSchemaForgot,
  validationSchemaSignUpHirist,
  validationSchemaOtp,
} from '../../../helpers/formValidator';
import {handleTracking} from '@jobseeker/common-core/utils';
import {
  login,
  register,
  forgotPassword,
  sendLoginOtp,
  verifyLoginOtp,
  sendMobileLoginOtp,
  verifyMobileLoginOtp,
  isEmailRequiredForRegistrationError,
  isEmailAlreadyExistsError,
  isOtpRateLimitError,
} from '../../../helpers';
import {useDispatch} from 'react-redux';
import ReCAPTCHA from 'react-google-recaptcha';
import type {} from '@jobseeker/lotus/mui.types';
import {openUrl, deferTask} from '@jobseeker/common-core/utils/common';
import FileUpload from '@jobseeker/lotus/components/FileUpload';
import DownloadAppOnJobdetail from '@jobseeker/lotus/components/DownloadAppJobdetailAndCatogary';
import {useValidateComponent, useWrappedTheme} from '@jobseeker/lotus/hooks';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import {Tooltip} from '@jobseeker/lotus/elements';
import {useInterceptor, useLoadAds} from '@jobseeker/common-core';
import {getRefTagAndOrigin} from '@jobseeker/common-core/utils';
import {useRouter} from 'next/router';
import {VersionVariants} from '@jobseeker/lotus/enums';
import Dialog from '@jobseeker/lotus/components/Dialog';
import {getAllParamsFromUrl} from '@jobseeker/common-core/utils/common';
import Link from '@mui/material/Link';
import GoogleLogin from '../GoogleLogin';
import AppleLogin from '../AppleLogin';
import {HiristTextLogoWhiteNew} from '@jobseeker/lotus/assets/images/resources/new-logo';

const REG_STEP_SIGN_IN = 'reg_step_sign_in';
const REG_STEP_SIGN_UP = 'reg_step_sign_up';
const REG_STEP_FORGOT_PASSWORD = 'reg_step_forgot_password';

const HIRIST_AUTH_REQUIRED: Record<string, 0 | 1> = {
  email: 1,
  password: 1,
  terms_and_policy: 1,
};

const makeAuthTracker =
  (regStep: string) =>
  (
    actionType: 'focus-in' | 'focus-out' | 'auto-fill',
    fieldName: string,
    fieldValue: string,
    isValid: 0 | 1,
    validationError: string,
    fieldValueSource?: 'resume parser' | 'user filled' | 'browser-autofill'
  ) => {
    trackFieldValueLevel({
      actionType,
      regStep,
      field_name: fieldName,
      field_value: fieldValue,
      field_value_source: fieldValueSource ?? 'user filled',
      required: HIRIST_AUTH_REQUIRED[fieldName] ?? 0,
      isValid,
      validation_error: validationError,
    });
  };

const trackSignIn = makeAuthTracker(REG_STEP_SIGN_IN);
const trackSignUp = makeAuthTracker(REG_STEP_SIGN_UP);
const trackForgotPassword = makeAuthTracker(REG_STEP_FORGOT_PASSWORD);

const AuthLeft = styled(Box)(({theme}) => ({
  position: 'relative',
  height: '100vh',
  width: '100%',
  padding: '310px 0',
}));

const SiteLogo = styled(Box)(({theme}) => ({
  position: 'absolute',
  top: 55,
  left: 70,
  '& img': {
    height: 40,
    filter: 'brightness(10)',
  },
}));
const CustomTextField = styled(TextField)(({theme}: {theme: any}) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      border: '2px solid #e6e6e6',
    },
    '&:hover fieldset': {
      border: '2px solid #e6e6e6',
    },
    '&.Mui-focused fieldset': {
      border: '2px solid #e6e6e6',
    },
  },
  '& .MuiOutlinedInput-input': {
    paddingTop: '19px',
    paddingBottom: '19px',
    paddingLeft: '15px',
    paddingRight: '15px',
    [theme.breakpoints.up('md')]: {
      paddingTop: 'clamp(10px, 1.8vh, 19px)',
      paddingBottom: 'clamp(10px, 1.8vh, 19px)',
    },
  },
}));

const OtpDigitField = styled(TextField)(({theme}: {theme: any}) => ({
  width: 42,
  height: 50,
  [theme.breakpoints.up('sm')]: {
    width: 50,
    height: 58,
  },
  [theme.breakpoints.up('md')]: {
    width: 54,
    height: 62,
  },
  '& .MuiOutlinedInput-root': {
    height: '100%',
    borderRadius: '8px',
    '& fieldset': {
      border: '2px solid #e6e6e6',
    },
    '&:hover fieldset': {
      border: '2px solid #e6e6e6',
    },
    '&.Mui-focused fieldset': {
      border: `2px solid ${theme.palette.primary.main}`,
    },
  },
  '& .MuiOutlinedInput-input': {
    height: '100%',
    padding: 0,
    textAlign: 'center',
    fontSize: '22px',
    fontWeight: 600,
    fontFamily: 'Nunito Sans',
    color: '#1c1c1c',
  },
}));

const PeopleSaying = styled(Box)(({theme}) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  textAlign: 'center',
  fontSize: 25,
  fontWeight: 800,
  lineHeight: 1.29,
  color: '#fff',
  padding: '0px 53px',
  '& p': {
    fontWeight: 'normal',
    lineHeight: 1.69,
    fontSize: 16,
  },
  [theme.breakpoints.down('lg')]: {
    padding: '0px 21px',
    position: 'relative',
    top: 'auto',
  },
}));
const FormHelperText = styled(MuiFormHelperText)(() => ({
  color: '#eb1c2a',
  marginTop: '-1px',
  fontSize: '14px',
  fontFamily: 'Nunito Sans',
}));

const CompanyList = styled(Box)(({theme}) => ({
  position: 'absolute',
  bottom: 30,
  width: '100%',
  '& p': {
    fontSize: 16,
    fontWeight: 'normal',
    color: '#fff',
    lineHeight: 1.69,
  },
  '& img': {
    margin: '0 10px',
    height: 40,
  },
}));

const HiristLoginForm = (props: any) => {
  const captchaRef = useRef<ReCAPTCHA>(null);
  const otpDigitRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showSignUpPassword, setShowSignupPassword] = useState(false);
  const [showForgotSuccess, setShowForgotSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(false);
  const [openDialog, isOpenDialog] = useState(false);
  const [resumeFile, setResumeFile] = useState<string | Blob>();
  const metaData: any = useInterceptor();
  const router = useRouter();
  const allparamsfromurl = getAllParamsFromUrl() || {};
  const metaJobIdMatch = router.asPath?.match(/-(\d+)(?:[/?#]|$)/);
  const metaJobId = metaJobIdMatch ? metaJobIdMatch[1] : '';
  const isTalentBridge = Boolean(
    router.asPath?.includes('talent-bridge') ||
      router.pathname?.includes('talent-bridge')
  );
  const regProfileSource = isTalentBridge ? 'reg_talentbridge' : 'reg_profile';
  const [openRegisterDialog, setOpenRegisterDialog] = useState(false);
  const params = new URLSearchParams(allparamsfromurl);
  const [handlePasswordRegexValidation, PasswordValidationComponent] =
    useValidateComponent(true);
  const theme = useWrappedTheme();
  const handleFileUpload = async (formData: FormData): Promise<boolean> => {
    try {
      formData.append('mediaType', 'TextResume');
      const uploadInfo = await mediaService.uploadFile(formData);
      if (uploadInfo?.url) {
        setUploadedFile(true);
        return true;
      }
      return false;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  const [apiError, setApiError] = useState<{
    message?: string;
    code?: number;
    activationToken?: string;
  }>({});

  const [isSignIn, setIsSignIn] = useState(false);
  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      reCaptchaCode: '',
    },
    validationSchema: validationSchemaLogin,
    onSubmit: async values => {
      setApiError({});
      setLoading(true);
      await handleAuthCall(values.email, values.password, values.reCaptchaCode);
    },
  });

  const formikSignUp = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: validationSchemaSignUpHirist,
    onSubmit: async values => {
      setApiError({});
      setLoading(true);
      const successfullregister = await handleRegisterCall(
        values.email?.trim(),
        values.password?.trim(),
        values.password?.trim(),
        true
      );
      const {refText} = getRefTagAndOrigin();
      handleTracking('jsActionRegister', {
        event_category: 'jsHomePage',
        Source: 'register',
        Ref: `${refText}`,
        Device: sessionStorage.getItem('device') || 'desktop',
        Device_Type: sessionStorage.getItem('deviceType') || 'web',
        ...(metaJobId && {content_type: 'product', content_id: [metaJobId]}),
        country: 'in',
        em: (values.email || '').toLowerCase(),
        fbc: allparamsfromurl.fbclid || '',
        fbp: (getCookie('_fbp') as string) || '',
        external_id: successfullregister?.userId
          ? String(successfullregister.userId)
          : '',
        source: regProfileSource,
      });
      const registerStatus = successfullregister?.valid
        ? 'signupSuccess'
        : 'signupFailure';
      handleUbaTracking(
        'jsActionRegisterClick',
        'click',
        registerStatus,
        successfullregister?.userId
      );
      if (successfullregister) {
        props.onSuccessFulRegister && props.onSuccessFulRegister();

        localStorage.removeItem('parsed-resume-content');
        if (resumeFile) {
          const formData = new FormData();
          formData.append('file', resumeFile);

          const uploadSuccess = await handleFileUpload(formData);

          if (uploadSuccess && (allparamsfromurl.ref ?? '') !== 'google') {
            params.set('nu', '1');

            try {
              const parseresume = await surveyService.parseResume(
                'HiristRegister'
              );
              localStorage.setItem(
                'parsed-resume-content',
                JSON.stringify(parseresume)
              );
            } catch (error) {
              console.error('Resume parsing failed:', error);
            }
          }
        }

        if (router?.asPath?.split('?')[0] === '/register') {
          params.set('profiletype', '1');
          params.set('nu', '');
          params.set('source', 'reg');
          openUrl(
            `/registration/resume-registration.php?${params.toString()}`,
            '_self'
          );
        } else {
          params.set('hir', '1');
          params.set('source', 'reg');

          const redirectToString = props?.customRedirectionUrl
            ? `&redirectto=${props?.customRedirectionUrl}`
            : '';

          openUrl(
            `/registration/addPersonalDetails?${params.toString()}${redirectToString}`,
            '_self'
          );
        }
      }

      setLoading(false);
    },
    validateOnChange: true,
    validateOnBlur: true,
    validate: async values => {
      handlePasswordRegexValidation(values.password);
    },
  });
  const formikForgot = useFormik({
    initialValues: {
      email: '',
      reCaptchaCode: '',
    },
    validationSchema: validationSchemaForgot,
    onSubmit: async values => {
      setApiError({});
      setLoading(true);
      await handleForgotPassword(values.email?.trim(), values.reCaptchaCode);
      setLoading(false);
      formikForgot.setFieldValue('reCaptchaCode', '');
    },
  });

  const isEmailOtpEnabled = process.env.NEXT_PUBLIC_EMAIL_OTP === 'true';

  const [isLoginOtp, setIsLoginOtp] = useState(true);
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [otpChannel, setOtpChannel] = useState<'email' | 'mobile'>('email');
  const [otpIdentifier, setOtpIdentifier] = useState('');
  const [otpIdentifierError, setOtpIdentifierError] = useState('');
  const [otpRegisterEmail, setOtpRegisterEmail] = useState('');
  const [otpRegisterEmailError, setOtpRegisterEmailError] = useState('');
  const [awaitingRegistrationEmail, setAwaitingRegistrationEmail] =
    useState(false);
  const [otpPhoneForRegistration, setOtpPhoneForRegistration] = useState('');
  const [otpAccountExists, setOtpAccountExists] = useState(false);

  const formikLoginOtp = useFormik({
    initialValues: {
      otp: '',
    },
    validationSchema: validationSchemaOtp,
    onSubmit: async values => {
      if (awaitingRegistrationEmail) {
        await handleSendEmailOtpForRegistration();
        return;
      }
      if (!needsEmail) {
        setApiError({});
      }
      await handleVerifyLoginOtp(values.otp);
    },
  });

  useEffect(() => {
    if (!isLoginOtp || !otpSent || otpTimer <= 0) {
      return;
    }
    const interval = setInterval(() => {
      setOtpTimer(seconds => (seconds > 0 ? seconds - 1 : 0));
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [isLoginOtp, otpSent, otpTimer]);

  useEffect(() => {
    if (isLoginOtp && otpSent) {
      otpDigitRefs.current[0]?.focus();
    }
  }, [isLoginOtp, otpSent]);

  useEffect(() => {
    if (isLoginOtp && !otpSent) {
      trackOtpUba(
        isSignIn ? 'loginOtpPageView' : 'registerOtpPageView',
        'view',
        isSignIn ? 'Page opened' : 'Pageopened'
      );
    }
  }, [isLoginOtp, otpSent, isSignIn]);

  useEffect(() => {
    if (isLoginOtp && otpSent) {
      trackOtpUba(
        'enterOtpPageView',
        'view',
        isSignIn ? 'Page opened' : 'Pageopened',
        undefined,
        otpChannel
      );
    }
  }, [isLoginOtp, otpSent, isSignIn, otpChannel]);

  let ref = '';
  if (window && window.location) {
    const urlParams = new URLSearchParams(window.location.search);
    ref = urlParams.get('ref') || '';
  }
  const [enableReCaptcha, setEnableReCaptcha] = useState(false);
  const [enableReCaptchaForgot, setEnableReCaptchaForgot] = useState(false);
  const dispatch = useDispatch();
  const handleReCAPTCHA = async (captchaCode: string | null) => {
    if (!captchaCode) {
      return;
    }

    formik.setFieldValue('reCaptchaCode', captchaCode);
    setApiError({});
  };
  const handleReCAPTCHAForgot = async (captchaCode: string | null) => {
    if (!captchaCode) {
      return;
    }

    formikForgot.setFieldValue('reCaptchaCode', captchaCode);
    setApiError({});
  };

  useEffect(() => {
    if (apiError && apiError.code && typeof apiError.code === 'number') {
      setLoading(false);
      if ([406].includes(apiError.code)) {
        if (isSignIn) {
          setEnableReCaptcha(true);
        } else if (isForgotPassword) {
          setEnableReCaptchaForgot(true);
        }
        if (apiError.code === 406) {
          captchaRef?.current?.reset();
        }
      }
    }
  }, [apiError, apiError?.code]);

  useEffect(() => {
    if (router.pathname === '/register') {
      setOpenRegisterDialog(true);
    } else {
      setOpenRegisterDialog(false);
    }
  }, [router.pathname]);

  useEffect(() => {
    if (apiError?.message) {
      setApiError({});
    }
    if (appleError) {
      setAppleError('');
    }
    setEnableReCaptcha(false);
  }, [formik.values.email, formik.values.password]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const appleErr = params.get('apple_error');
    if (!appleErr) {
      return;
    }
    setAppleError(
      appleErr === 'ACCOUNT_NOT_FOUND'
        ? 'It looks like you haven’t signed up with Apple yet. Please create your account using an iPhone or iPad to continue.'
        : decodeURIComponent(appleErr)
    );
    params.delete('apple_error');
    const cleaned = `${window.location.pathname}${
      params.toString() ? '?' + params.toString() : ''
    }${window.location.hash}`;
    window.history.replaceState({}, '', cleaned);
  }, []);

  useEffect(() => {
    if (appleError) {
      setAppleError('');
    }
  }, [otpIdentifier, otpRegisterEmail]);

  useEffect(() => {
    if (apiError?.message) {
      setApiError({});
    }
  }, [formikSignUp.values.email, formikSignUp.values.password]);

  useEffect(() => {
    if (apiError?.message) {
      setApiError({});
    }
    setEnableReCaptchaForgot(false);
  }, [formikForgot.values.email]);

  const handleAuthCall = async (
    email: string,
    password: string,
    grecaptcha?: string
  ) => {
    try {
      setApiError({});
      const authInfo = await login(email, password, dispatch, grecaptcha);
      const {refText} = getRefTagAndOrigin();

      handleTracking('jsActionLogin', {
        event_category: 'jsHomePage',
        Source: 'login',
        Ref: `${refText}`,
        Device: sessionStorage.getItem('device') || 'desktop',
        Device_Type: sessionStorage.getItem('deviceType') || 'web',
        ...(metaJobId && {content_type: 'product', content_id: [metaJobId]}),
        country: 'in',
        em: (email || '').toLowerCase(),
        fbc: allparamsfromurl.fbclid || '',
        fbp: (getCookie('_fbp') as string) || '',
        external_id: authInfo?.userId ? String(authInfo.userId) : '',
      });

      if (authInfo?.activationToken) {
        setLoading(false);
        setApiError({
          message: authInfo.message,
          code: 10999,
          activationToken: authInfo.activationToken,
        });
        handleUbaTracking('jsActionLoginClick', 'click', 'signinFailure');
        return;
      }

      if (authInfo?.options?.reset) {
        setApiError({
          message: authInfo.message,
          code: 10203,
        });
        setLoading(false);
        handleUbaTracking('jsActionLoginClick', 'click', 'signinFailure');
        return;
      }

      if (props.onSuccess && authInfo['token']) {
        handleUbaTracking(
          'jsActionLoginClick',
          'click',
          'signinSuccess',
          authInfo?.userId
        );
        props.onSuccess();
        return;
      }

      if (authInfo['token']) {
        handleUbaTracking(
          'jsActionLoginClick',
          'click',
          'signinSuccess',
          authInfo?.userId
        );
        if (window && window.location) {
          const urlParams = new URLSearchParams(window.location.search);
          const redirect = urlParams.get('redirectto');
          if (redirect) {
            openUrl(redirect, '_self');
            return;
          }
        }
        openUrl('/jobfeed', '_self');
      }

      setEnableReCaptcha(false);
    } catch (error) {
      if (typeof error === 'object' && error) {
        setApiError(error);
      }
      try {
        const errObj: any = error || {};
        const msg: string = (errObj?.message || '').toString();
        const isIncorrect = /incorrect|invalid/i.test(msg);
        const loginStatus = isIncorrect
          ? 'incorrectEmailorPass'
          : 'signinFailure';
        handleUbaTracking('jsActionLoginClick', 'click', loginStatus);
      } catch (_) {
        handleUbaTracking('jsActionLoginClick', 'click', 'signinFailure');
      }
      setLoading(false);
    }
    setEnableReCaptcha(false);
  };

  const handleRegisterCall = async (
    email: string,
    password: string,
    confirmPassword: string,
    policyCheck: boolean
  ) => {
    try {
      setLoading(true);
      const regInfo = await register(
        email,
        password,
        confirmPassword,
        policyCheck,
        dispatch
      );
      handleTracking('register_first_step', {
        event_category: 'action',
        Label: 'SignUp User',
        Value: 'success',
        ...(metaJobId && {content_type: 'product', content_id: [metaJobId]}),
        country: 'in',
        ph: '',
        em: (email || '').toLowerCase(),
        fbc: allparamsfromurl.fbclid || '',
        fbp: (getCookie('_fbp') as string) || '',
        external_id: regInfo?.userId ? String(regInfo.userId) : '',
        source: regProfileSource,
      });
      return regInfo;
    } catch (error) {
      setLoading(false);
      if (typeof error === 'object' && error) {
        setApiError(error);
      }
    }
  };
  // const [showSignUpPassword, setShowSignupPassword] = useState(false);
  const [open, setOpen] = useState(false);

  const handleTooltipOpen = () => setOpen(true);
  const handleTooltipClose = () => setOpen(false);

  const handleForgotPassword = async (email: string, grecaptcha?: string) => {
    try {
      setLoading(true);
      const message = await forgotPassword(email, grecaptcha);
      if (message?.activationToken) {
        setLoading(false);
        setApiError({
          message: message.message,
          code: 10999,
          activationToken: message.activationToken,
        });
        handleUbaTracking('forgotPassClick', 'click', 'reactivationRequired');
        return;
      }

      setShowForgotSuccess(true);
      setApiError({message, code: 0});
      handleUbaTracking('forgotPassClick', 'click', 'success');
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setShowForgotSuccess(false);
      if (typeof error === 'object' && error) {
        setApiError(error);
      }
      handleUbaTracking('forgotPassClick', 'click', 'failure');
    }
    setEnableReCaptchaForgot(false);
  };

  const EMAIL_REGEX =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  const PHONE_REGEX = /^\d{10}$/;
  const needsEmail =
    awaitingRegistrationEmail ||
    (otpChannel === 'mobile' &&
      (isEmailRequiredForRegistrationError(apiError?.code) ||
        isEmailAlreadyExistsError(apiError?.code)));

  useEffect(() => {
    if (isLoginOtp && otpSent && !needsEmail && !loading && apiError?.message) {
      otpDigitRefs.current[5]?.focus();
    }
  }, [apiError?.message, isLoginOtp, otpSent, needsEmail, loading]);

  const onOtpIdentifierChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setOtpIdentifier(event.target.value);
    setOtpIdentifierError('');
    if (apiError?.message) {
      setApiError({});
    }
  };

  const onOtpRegisterEmailChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setOtpRegisterEmail(event.target.value);
    setOtpRegisterEmailError('');
    setApiError(prev => ({...prev, message: ''}));
  };

  const handleSendLoginOtp = async () => {
    const identifier = otpIdentifier?.trim();
    const isPhone = PHONE_REGEX.test(identifier || '');
    const isEmail = EMAIL_REGEX.test(identifier || '');
    trackOtpUba(
      isSignIn ? 'loginOtpPageClick' : 'registerOtpPageClick',
      'click',
      'Getotp',
      undefined,
      isPhone ? 'mobile' : isEmail ? 'email' : undefined
    );
    if (!isPhone && !isEmail) {
      setOtpIdentifierError(
        'Enter a valid email address or 10-digit mobile number'
      );
      return;
    }
    const channel: 'email' | 'mobile' = isPhone ? 'mobile' : 'email';
    setOtpChannel(channel);
    try {
      setLoading(true);
      setApiError({});
      const sendResult =
        channel === 'mobile'
          ? await sendMobileLoginOtp(
              identifier,
              isSignIn ? 'login' : 'register'
            )
          : await sendLoginOtp(identifier, isSignIn ? 'login' : 'register');
      setOtpAccountExists(Boolean(sendResult?.name));
      formikLoginOtp.setFieldValue('otp', '');
      setOtpSent(true);
      setOtpTimer(60);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      if (typeof error === 'object' && error) {
        if (
          channel === 'mobile' &&
          isEmailRequiredForRegistrationError((error as {code?: number})?.code)
        ) {
          setAwaitingRegistrationEmail(true);
          setOtpPhoneForRegistration(identifier);
          setApiError({});
        } else {
          setApiError(error);
        }
      }
    }
  };

  const handleSendEmailOtpForRegistration = async () => {
    const email = otpRegisterEmail?.trim();
    if (!EMAIL_REGEX.test(email)) {
      setOtpRegisterEmailError('Enter a valid email address');
      return;
    }
    try {
      setLoading(true);
      const sendResult = await sendLoginOtp(
        email,
        isSignIn ? 'login' : 'register'
      );
      setOtpChannel('email');
      setOtpIdentifier(email);
      setOtpAccountExists(Boolean(sendResult?.name));
      setAwaitingRegistrationEmail(false);
      formikLoginOtp.setFieldValue('otp', '');
      setOtpSent(true);
      setOtpTimer(60);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      if (typeof error === 'object' && error) {
        setApiError(error);
      }
    }
  };

  const handleResendLoginOtp = async () => {
    if (otpTimer > 0 || isOtpRateLimitError(apiError?.message)) {
      return;
    }
    try {
      setApiError({});
      if (otpChannel === 'mobile') {
        await sendMobileLoginOtp(
          otpIdentifier?.trim(),
          isSignIn ? 'login' : 'register'
        );
      } else {
        await sendLoginOtp(
          otpIdentifier?.trim(),
          isSignIn ? 'login' : 'register'
        );
      }
      formikLoginOtp.setFieldValue('otp', '');
      setOtpTimer(60);
    } catch (error) {
      if (typeof error === 'object' && error) {
        setApiError(error);
      }
    }
  };

  const handleChangeLoginOtpIdentifier = () => {
    trackOtpUba('enterOtpPageClick', 'click', 'Change', undefined, otpChannel);
    setApiError({});
    formikLoginOtp.setFieldValue('otp', '');
    setOtpSent(false);
    setOtpTimer(0);
    setOtpRegisterEmail('');
    setOtpRegisterEmailError('');
    setAwaitingRegistrationEmail(false);
    setOtpPhoneForRegistration('');
    setOtpAccountExists(false);
  };

  const getRegistrationTrackingParams = (stateUrl?: string | null): string => {
    if (!stateUrl) return '';
    try {
      const url = new URL(stateUrl, window.location.origin);
      const keys = [
        'utm_source',
        'utm_campaign',
        'utm_medium',
        'utm_term',
        'utm_content',
        'ref',
      ];
      const out = new URLSearchParams();
      keys.forEach(k => {
        const v = url.searchParams.get(k);
        if (v) out.set(k, v);
      });
      return out.toString() ? `&${out.toString()}` : '';
    } catch {
      return '';
    }
  };

  const handleVerifyLoginOtp = async (otp: string) => {
    if (isOtpRateLimitError(apiError?.message)) {
      return;
    }
    try {
      setLoading(true);
      const authInfo =
        otpChannel === 'mobile'
          ? await verifyMobileLoginOtp(
              otpIdentifier?.trim(),
              otp,
              dispatch,
              isSignIn ? 'login' : 'register',
              otpRegisterEmail?.trim() || undefined
            )
          : await verifyLoginOtp(
              otpIdentifier?.trim(),
              otp,
              dispatch,
              isSignIn ? 'login' : 'register',
              otpPhoneForRegistration || undefined
            );
      const {refText} = getRefTagAndOrigin();
      handleTracking('jsActionLogin', {
        event_category: 'jsHomePage',
        Source: 'login',
        Ref: `${refText}`,
        Device: sessionStorage.getItem('device') || 'desktop',
        Device_Type: sessionStorage.getItem('deviceType') || 'web',
        ...(metaJobId && {content_type: 'product', content_id: [metaJobId]}),
        country: 'in',
        em: otpChannel === 'email' ? (otpIdentifier || '').toLowerCase() : '',
        fbc: allparamsfromurl.fbclid || '',
        fbp: (getCookie('_fbp') as string) || '',
        external_id: authInfo?.userId ? String(authInfo.userId) : '',
      });

      trackOtpUba(
        'enterOtpPageClick',
        'click',
        'verify&login',
        'success',
        otpChannel
      );

      const isVerifySuccess = authInfo['token'];
      if (props.onSuccess && isVerifySuccess) {
        props.onSuccess();
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const redirect = urlParams.get('redirectto');

      if (authInfo?.redirectPath === '/registration') {
        const tracking = getRegistrationTrackingParams(redirect);
        openUrl(
          `/registration/addPersonalDetails?hir=1&source=reg&redirectto=${encodeURIComponent(
            redirect || '/jobfeed'
          )}${tracking}`,
          '_self'
        );
        return;
      }

      if (redirect) {
        openUrl(redirect, '_self');
        return;
      }
      openUrl(authInfo?.redirectPath || '/jobfeed', '_self');
    } catch (error) {
      trackOtpUba(
        'enterOtpPageClick',
        'click',
        'verify&login',
        !isSignIn ||
          (otpChannel === 'mobile' &&
            (isEmailRequiredForRegistrationError(
              (error as {code?: number})?.code
            ) ||
              isEmailAlreadyExistsError((error as {code?: number})?.code)))
          ? 'reg'
          : 'failure',
        otpChannel
      );
      setLoading(false);
      if (typeof error === 'object' && error) {
        setApiError(error);
      }
    }
  };

  const toggleLoginOtp = () => {
    if (!isEmailOtpEnabled) return;
    if (!isLoginOtp) {
      trackOtpUba(
        isSignIn ? 'loginPageClick' : 'registrationInitialPageClick',
        'click',
        isSignIn ? 'Loginwithotp' : 'Registerwithotp'
      );
    } else {
      trackOtpUba(
        otpSent
          ? 'enterOtpPageClick'
          : isSignIn
          ? 'loginOtpPageClick'
          : 'registerOtpPageClick',
        'click',
        isSignIn ? 'usePasswordToLogin' : 'registerwithpassword'
      );
    }
    deferTask(() => {
      setApiError({});
      setAppleError('');
      setForgotPassword(false);
      setEnableReCaptcha(false);
      setOtpSent(false);
      setOtpTimer(0);
      if (isLoginOtp) {
        if (otpIdentifier && EMAIL_REGEX.test(otpIdentifier.trim())) {
          if (isSignIn) {
            formik.setFieldValue('email', otpIdentifier);
          } else {
            formikSignUp.setFieldValue('email', otpIdentifier);
          }
        }
        setOtpIdentifier('');
      } else {
        setOtpIdentifier(
          (isSignIn ? formik.values.email : formikSignUp.values.email) || ''
        );
      }
      if (otpRegisterEmail && EMAIL_REGEX.test(otpRegisterEmail.trim())) {
        formikSignUp.setFieldValue('email', otpRegisterEmail);
      }
      setOtpIdentifierError('');
      setOtpChannel('email');
      setOtpRegisterEmail('');
      setOtpRegisterEmailError('');
      setAwaitingRegistrationEmail(false);
      setOtpPhoneForRegistration('');
      setOtpAccountExists(false);
      formikLoginOtp.setErrors({});
      setIsLoginOtp(prev => !prev);
    });
  };

  const handleDownloadClick = async () => {
    deferTask(() => {
      handleTracking('jsClickDwdAppBeforeLogin', {
        event_category: 'MobileAppPromotion',
        Origin: isSignIn ? 'SigninPage' : 'SignUpPage',
        Device: sessionStorage.getItem('device') || 'desktop',
        Device_Type: sessionStorage.getItem('deviceType') || 'web',
        Status: sessionStorage.getItem('status') || 'LoggedOut',
      });
      if (isSignIn) {
        openUrl('/downloadapp?ref=signin');
      } else openUrl('/downloadapp?ref=signup');
    });
  };
  useEffect(() => {
    const {refText} = getRefTagAndOrigin();
    handleTracking('jsViewLoginLogo', {
      event_category: 'jsHomePage',
      Source: 'register',
      Ref: `${refText}`,
      Device: sessionStorage.getItem('device') || 'desktop',
      Device_Type: sessionStorage.getItem('deviceType') || 'web',
    });
  }, []);

  useEffect(() => {
    setIsSignIn(props?.enableSignIn ?? false);
  }, [props?.enableSignIn]);

  const trackOtpUba = (
    eventName: string,
    actionType: 'view' | 'click',
    action: string,
    status?: 'success' | 'failure' | 'reg',
    actionSrc?: 'email' | 'mobile'
  ) => {
    TrackEventUBA(eventName, actionType, {
      pageName: getUbaPageName(),
      action,
      ...(status ? {status} : {}),
      ...(actionSrc ? {actionSrc} : {}),
    });
  };

  const handleUbaTracking = (
    eventName: any,
    action: any,
    status?: string,
    sessionUsername?: string
  ) => {
    const resolvedPage = openRegisterDialog
      ? 'Register Campaign'
      : getUbaPageName();
    TrackEventUBA(eventName, action, {
      page: resolvedPage,
      pageName: resolvedPage,
      ref: router.query.ref || '',
      ...(status ? {status} : {}),
      sessionUsername:
        sessionStorage.getItem('userId') || sessionUsername || '',
    });
  };

  const handlebackclick = async () => {
    deferTask(() => {
      setForgotPassword(false);
      setApiError({});
      setEnableReCaptchaForgot(false);
      setIsSignIn(true);
    });
  };

  const [isForgotPassword, setForgotPassword] = useState(false);
  const [appleError, setAppleError] = useState('');

  const signInEmailFocused = useRef(false);
  const signInPasswordFocused = useRef(false);
  const signUpEmailFocused = useRef(false);
  const signUpPasswordFocused = useRef(false);
  const forgotEmailFocused = useRef(false);
  const emailAutoFillTracked = useRef(false);
  const passwordAutoFillTracked = useRef(false);

  useEffect(() => {
    // Reset per form switch so each form gets its own autofill tracking.
    emailAutoFillTracked.current = false;
    passwordAutoFillTracked.current = false;

    const emailEl = document.querySelector<HTMLInputElement>(
      'input[name="email"]'
    );
    const passEl = document.querySelector<HTMLInputElement>(
      'input[name="password"]'
    );

    const isAutofilled = (el: HTMLInputElement | null) => {
      if (!el) return false;
      try {
        return el.matches(':-webkit-autofill') || el.matches(':autofill');
      } catch {
        return false;
      }
    };

    const trackEmailAutofill = () => {
      if (emailAutoFillTracked.current) return;
      emailAutoFillTracked.current = true;
      if (isSignIn)
        trackSignIn(
          'auto-fill',
          'email',
          '**redacted**',
          1,
          '',
          'browser-autofill'
        );
      else if (!isForgotPassword)
        trackSignUp(
          'auto-fill',
          'email',
          '**redacted**',
          1,
          '',
          'browser-autofill'
        );
      else
        trackForgotPassword(
          'auto-fill',
          'email',
          '**redacted**',
          1,
          '',
          'browser-autofill'
        );
    };

    const trackPasswordAutofill = () => {
      if (passwordAutoFillTracked.current || isForgotPassword) return;
      passwordAutoFillTracked.current = true;
      if (isSignIn)
        trackSignIn(
          'auto-fill',
          'password',
          '**redacted**',
          1,
          '',
          'browser-autofill'
        );
      else
        trackSignUp(
          'auto-fill',
          'password',
          '**redacted**',
          1,
          '',
          'browser-autofill'
        );
    };

    const trackIfAutofilled = () => {
      if (isAutofilled(emailEl)) trackEmailAutofill();
      if (isAutofilled(passEl)) trackPasswordAutofill();
    };

    // animationstart: catches autofill that fires after listeners attach. Chrome
    // suppresses CSS animations on autofilled type="password" inputs, so this may
    // not fire for the password field — the 'input' listeners below cover that case.
    const onAutofill = (e: AnimationEvent) => {
      if (e.animationName !== 'mui-auto-fill') return;
      const target = e.target as HTMLInputElement;
      if (target === emailEl) trackEmailAutofill();
      else if (target === passEl) trackPasswordAutofill();
    };

    // Always-active 'input' listeners: Chrome fires 'input' when credentials are
    // selected from the browser dropdown. The isAutofilled guard ensures manual
    // keystrokes don't trigger tracking. Resetting the tracked ref on manual edits
    // means a subsequent browser autofill on that field (e.g. user changes email
    // and browser suggests new credentials) is captured fresh.
    const onEmailInput = () => {
      if (!isAutofilled(emailEl)) emailAutoFillTracked.current = false;
      setTimeout(trackIfAutofilled, 50);
    };
    const onPassInput = () => {
      if (!isAutofilled(passEl)) passwordAutoFillTracked.current = false;
      setTimeout(trackIfAutofilled, 50);
    };

    emailEl?.addEventListener('animationstart', onAutofill as EventListener);
    passEl?.addEventListener('animationstart', onAutofill as EventListener);
    emailEl?.addEventListener('input', onEmailInput);
    passEl?.addEventListener('input', onPassInput);

    return () => {
      emailEl?.removeEventListener(
        'animationstart',
        onAutofill as EventListener
      );
      passEl?.removeEventListener(
        'animationstart',
        onAutofill as EventListener
      );
      emailEl?.removeEventListener('input', onEmailInput);
      passEl?.removeEventListener('input', onPassInput);
    };
  }, [isSignIn, isForgotPassword]);

  const toggleAuthMode = () => {
    deferTask(() => {
      setAppleError('');
      setOtpSent(false);
      setOtpTimer(0);
      setOtpIdentifier('');
      setOtpIdentifierError('');
      setOtpRegisterEmail('');
      setOtpRegisterEmailError('');
      setAwaitingRegistrationEmail(false);
      setOtpPhoneForRegistration('');
      setOtpAccountExists(false);
      formikLoginOtp.setFieldValue('otp', '');
      formikLoginOtp.setErrors({});
      if (isSignIn) {
        setApiError({});
        setIsSignIn(false);
        handleUbaTracking(
          openRegisterDialog
            ? 'registrationInitialOverlayPageView'
            : 'registrationInitialPageView',
          'view'
        );
        setForgotPassword(false);
      } else {
        handleUbaTracking('jsLoginLogoView', 'view', 'signin');
        handleUbaTracking('loginPageView', 'view');
        setApiError({});
        setIsSignIn(true);
        setForgotPassword(false);
      }
    });
  };
  const [companies, setCompanies] = useState<any>([]);
  const [ads, setAds] = useState<any>([]);
  useLoadAds(metaData.ads?.favouriteCompanies?.slot, setAds, {}, false);
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        if (ads && ads.length > 0) {
          setCompanies(ads);
        }
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    };

    fetchCompanies();
  }, [ads]);
  const appleErrorMessage = appleError ? (
    <Typography
      component="p"
      fontSize="13px"
      color="error.main"
      textAlign="center"
      marginTop="10px"
      data-testid="apple-login-error"
    >
      {appleError}
    </Typography>
  ) : null;

  const renderForm = () => {
    if (isForgotPassword) {
      return (
        <Box
          component={'form'}
          noValidate
          onSubmit={event => {
            event.preventDefault();
            deferTask(() => {
              formikForgot.handleSubmit();
            });
          }}
        >
          <Typography
            mb={'-10px'}
            fontWeight={700}
            color={'#000'}
            fontFamily={'Nunito Sans'}
            fontSize={'14px'}
            sx={{
              opacity: 0.8,
            }}
          >
            Email Address
          </Typography>
          <CustomTextField
            fullWidth
            type="email"
            margin="normal"
            placeholder="you@example.com"
            name="email"
            value={formikForgot.values.email}
            onChange={formikForgot.handleChange}
            onFocus={() => {
              forgotEmailFocused.current = true;
              trackForgotPassword('focus-in', 'email', '**redacted**', 1, '');
            }}
            onBlur={async (e: any) => {
              const err = await formikForgot.validateField('email');
              trackForgotPassword(
                'focus-out',
                'email',
                '**redacted**',
                err ? 0 : 1,
                err ? String(err) : ''
              );
            }}
          />
          {formikForgot.touched.email && Boolean(formikForgot.errors.email) && (
            <FormHelperText>{formikForgot.errors.email}</FormHelperText>
          )}
          {enableReCaptchaForgot && (
            <Box display="flex" justifyContent="flex-start" mt={2}>
              <ReCAPTCHA
                ref={captchaRef}
                sitekey={`${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}`}
                size="normal"
                onChange={handleReCAPTCHAForgot}
              />
            </Box>
          )}
          {apiError &&
            apiError.message !== '' &&
            apiError.message !== undefined && (
              <Typography
                component="p"
                fontSize="13px"
                color={'error.main'}
                display="inline-flex"
                flexDirection="column"
                textAlign="center"
                marginTop="10px"
              >
                {apiError.message}
                {apiError?.code === 10999 && (
                  <Box>
                    <Link
                      sx={{
                        textDecoration: 'underline',
                        textDecorationColor: theme.palette.primary.main,
                        '&:hover': {
                          textDecorationColor: theme.palette.primary.main,
                        },
                        textAlign: 'left',
                      }}
                      href={`reactivate-account${
                        apiError?.activationToken
                          ? `?em=${btoa(
                              formik.values.email
                            )}&token=${encodeURIComponent(
                              apiError?.activationToken
                            )}`
                          : ''
                      }`}
                    >
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 500,
                          lineHeight: '16px',
                          color: 'primary.main',
                          marginTop: 0.5,
                        }}
                      >
                        Reactivate your account
                      </Typography>
                    </Link>
                  </Box>
                )}
              </Typography>
            )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            // disabled={loading}
            sx={{
              padding: '12px',
              fontSize: '20px',
              fontFamily: 'Nunito Sans',
              borderRadius: '8px',
              mt: 2,
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
                boxShadow: '0px 6px 18px rgba(233, 99, 12, 0.19)',
              },
            }}
          >
            {loading ? (
              <Loader
                size={35}
                sx={{
                  color: '#ffffff !important',
                }}
              />
            ) : (
              'Reset Password'
            )}
          </Button>
        </Box>
      );
    }

    if (isLoginOtp) {
      return (
        <Box
          component={'form'}
          noValidate
          onSubmit={event => {
            event.preventDefault();
            if (!otpSent && !awaitingRegistrationEmail) {
              return;
            }
            deferTask(() => {
              formikLoginOtp.handleSubmit();
            });
          }}
        >
          {needsEmail ? (
            <>
              <Typography
                mb={'-10px'}
                fontWeight={700}
                color={'#000'}
                fontSize={'15px'}
                fontFamily={'Nunito Sans'}
                sx={{opacity: 0.8}}
              >
                Email Address
              </Typography>
              <CustomTextField
                fullWidth
                type="email"
                margin="normal"
                placeholder="Enter your email address"
                name="otpRegisterEmail"
                value={otpRegisterEmail}
                onChange={onOtpRegisterEmailChange}
                disabled={loading}
                error={Boolean(otpRegisterEmailError)}
              />
              {otpRegisterEmailError && (
                <FormHelperText error>{otpRegisterEmailError}</FormHelperText>
              )}

              {awaitingRegistrationEmail && (
                <Typography
                  component="p"
                  fontSize="13px"
                  color={'error.main'}
                  textAlign="center"
                  marginTop="10px"
                >
                  {isSignIn
                    ? "The number isn't registered with us. Please provide your email to continue"
                    : 'Please provide your email to continue the registration process'}
                </Typography>
              )}

              {apiError &&
                apiError.message !== '' &&
                apiError.message !== undefined && (
                  <Typography
                    component="p"
                    fontSize="13px"
                    color={'error.main'}
                    textAlign="center"
                    marginTop="10px"
                  >
                    {apiError.message}
                  </Typography>
                )}

              <Button
                type={awaitingRegistrationEmail ? 'button' : 'submit'}
                fullWidth
                disabled={
                  awaitingRegistrationEmail
                    ? !EMAIL_REGEX.test((otpRegisterEmail || '').trim())
                    : (formikLoginOtp.values.otp || '').length !== 6 ||
                      !EMAIL_REGEX.test((otpRegisterEmail || '').trim()) ||
                      isOtpRateLimitError(apiError?.message)
                }
                onClick={
                  awaitingRegistrationEmail
                    ? handleSendEmailOtpForRegistration
                    : undefined
                }
                sx={{
                  padding: {xxs: '12px', md: 'clamp(8px, 1.4vh, 12px)'},
                  backgroundColor: theme.palette.primary.main,
                  fontSize: {xxs: '20px', md: 'clamp(16px, 2.2vh, 20px)'},
                  fontStyle: 'Nunito sans',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  mt: 2,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main,
                    boxShadow: '0px 6px 18px rgba(233, 99, 12, 0.19)',
                  },
                  '&.Mui-disabled': {
                    color: 'white',
                    background: 'rgb(249, 183, 163)',
                    cursor: 'not-allowed',
                  },
                }}
              >
                {loading ? (
                  <Loader size={35} sx={{color: '#ffffff !important'}} />
                ) : (
                  'Register'
                )}
              </Button>
            </>
          ) : !otpSent ? (
            <>
              <Typography
                mb={'-10px'}
                fontWeight={700}
                color={'#000'}
                fontSize={'15px'}
                fontFamily={'Nunito Sans'}
                sx={{opacity: 0.8}}
              >
                Email or Mobile Number
              </Typography>
              <CustomTextField
                fullWidth
                type="text"
                margin="normal"
                placeholder="Email or Mobile Number"
                name="otpIdentifier"
                value={otpIdentifier}
                onChange={onOtpIdentifierChange}
                onFocus={() =>
                  trackOtpUba(
                    isSignIn ? 'loginOtpPageClick' : 'registerOtpPageClick',
                    'click',
                    isSignIn ? 'email_Id' : 'emailid'
                  )
                }
                error={Boolean(otpIdentifierError)}
                InputProps={{
                  startAdornment: PHONE_REGEX.test(
                    (otpIdentifier || '').trim()
                  ) ? (
                    <InputAdornment position="start">
                      <Box display="flex" alignItems="center" gap="8px">
                        <Image
                          src={indianFLag}
                          alt="indianFLag"
                          width={20}
                          height={14}
                        />
                        <Typography>{'+91'}</Typography>
                      </Box>
                    </InputAdornment>
                  ) : undefined,
                }}
              />
              {otpIdentifierError && (
                <FormHelperText error>{otpIdentifierError}</FormHelperText>
              )}
              <Typography
                mt={1}
                fontSize={'13px'}
                color={'#7e7e7e'}
                fontFamily={'Nunito Sans'}
              >
                {'You will receive one time password on this.'}
              </Typography>

              {apiError &&
                apiError.message !== '' &&
                apiError.message !== undefined && (
                  <Typography
                    component="p"
                    fontSize="13px"
                    color={'error.main'}
                    textAlign="center"
                    marginTop="10px"
                  >
                    {apiError.message}
                  </Typography>
                )}

              <Button
                type="button"
                fullWidth
                disabled={
                  (!PHONE_REGEX.test((otpIdentifier || '').trim()) &&
                    !EMAIL_REGEX.test((otpIdentifier || '').trim())) ||
                  Boolean(otpIdentifierError)
                }
                onClick={handleSendLoginOtp}
                sx={{
                  padding: {xxs: '12px', md: 'clamp(8px, 1.4vh, 12px)'},
                  backgroundColor: theme.palette.primary.main,
                  fontSize: {xxs: '20px', md: 'clamp(16px, 2.2vh, 20px)'},
                  fontStyle: 'Nunito sans',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  mt: {xxs: '30px', md: 'clamp(12px, 2.5vh, 44px)'},
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main,
                    boxShadow: '0px 6px 18px rgba(233, 99, 12, 0.19)',
                  },
                  '&.Mui-disabled': {
                    color: 'white',
                    background: 'rgb(249, 183, 163)',
                    cursor: 'not-allowed',
                  },
                }}
              >
                {loading ? (
                  <Loader
                    size={35}
                    sx={{
                      color: '#ffffff !important',
                    }}
                  />
                ) : (
                  'Get OTP'
                )}
              </Button>
            </>
          ) : (
            <>
              <Typography
                fontSize={'14px'}
                color={'#000'}
                fontFamily={'Nunito Sans'}
                sx={{opacity: 0.8, wordBreak: 'break-word'}}
              >
                {"We've sent a 6-digit code to "}
                <strong
                  style={{
                    display: 'inline-block',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    verticalAlign: 'bottom',
                  }}
                >
                  {otpChannel === 'mobile'
                    ? `+91 ${otpIdentifier}`
                    : otpIdentifier}
                </strong>
                <Typography
                  component="span"
                  onClick={loading ? undefined : handleChangeLoginOtpIdentifier}
                  sx={{
                    color: 'rgb(59, 111, 224)',
                    fontWeight: 700,
                    marginLeft: '8px',
                    whiteSpace: 'nowrap',
                    ...(loading
                      ? {cursor: 'not-allowed', opacity: 0.5}
                      : {cursor: 'pointer'}),
                  }}
                >
                  Change
                </Typography>
              </Typography>

              <Typography
                mt={2}
                mb={1}
                fontWeight={700}
                color={'#000'}
                fontSize={'15px'}
                fontFamily={'Nunito Sans'}
                sx={{opacity: 0.8}}
              >
                Enter OTP
              </Typography>
              <Box display="flex" gap={1.25}>
                {Array.from({length: 6}).map((_, index) => (
                  <OtpDigitField
                    key={index}
                    value={(formikLoginOtp.values.otp || '')[index] || ''}
                    disabled={loading}
                    inputRef={(el: HTMLInputElement | null) => {
                      otpDigitRefs.current[index] = el;
                    }}
                    inputProps={{
                      inputMode: 'numeric',
                      maxLength: 1,
                    }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const digit = (e.target.value || '')
                        .replace(/\D/g, '')
                        .slice(-1);
                      const chars = (formikLoginOtp.values.otp || '').split('');
                      chars[index] = digit;
                      const next = chars.join('');
                      formikLoginOtp.setFieldValue('otp', next);
                      if (digit && index < 5) {
                        otpDigitRefs.current[index + 1]?.focus();
                      }
                      if (digit && index === 5 && next.length === 6) {
                        setTimeout(() => formikLoginOtp.handleSubmit(), 200);
                      }
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      const otp = formikLoginOtp.values.otp || '';
                      if (e.key === 'Backspace' && !otp[index] && index > 0) {
                        otpDigitRefs.current[index - 1]?.focus();
                      } else if (e.key === 'ArrowLeft' && index > 0) {
                        otpDigitRefs.current[index - 1]?.focus();
                      } else if (e.key === 'ArrowRight' && index < 5) {
                        otpDigitRefs.current[index + 1]?.focus();
                      } else if (e.key === 'Enter') {
                        formikLoginOtp.handleSubmit();
                      }
                    }}
                    onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                      const pasted = (e.clipboardData.getData('text') || '')
                        .replace(/\D/g, '')
                        .slice(0, 6);
                      if (!pasted) return;
                      e.preventDefault();
                      formikLoginOtp.setFieldValue('otp', pasted);
                      const lastIndex = Math.min(pasted.length, 5);
                      otpDigitRefs.current[lastIndex]?.focus();
                      if (pasted.length === 6) {
                        setTimeout(() => formikLoginOtp.handleSubmit(), 200);
                      }
                    }}
                  />
                ))}
              </Box>

              <Box display="flex" mt={1.5}>
                {otpTimer > 0 && !isOtpRateLimitError(apiError?.message) ? (
                  <Typography
                    fontSize={'12px'}
                    color={'#7e7e7e'}
                    fontFamily={'Nunito Sans'}
                  >
                    {`Resend OTP in ${String(
                      Math.floor(otpTimer / 60)
                    ).padStart(2, '0')}:${String(otpTimer % 60).padStart(
                      2,
                      '0'
                    )}`}
                  </Typography>
                ) : (
                  <Typography
                    fontSize={'12px'}
                    color={'#7e7e7e'}
                    fontFamily={'Nunito Sans'}
                  >
                    {"Didn't receive an OTP? "}
                    <Typography
                      component="span"
                      onClick={
                        isOtpRateLimitError(apiError?.message) || loading
                          ? undefined
                          : handleResendLoginOtp
                      }
                      sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        ...(isOtpRateLimitError(apiError?.message) || loading
                          ? {
                              opacity: 0.5,
                              cursor: 'not-allowed',
                              pointerEvents: 'none',
                            }
                          : {cursor: 'pointer'}),
                      }}
                    >
                      Resend OTP
                    </Typography>
                  </Typography>
                )}
              </Box>

              {apiError &&
                apiError.message !== '' &&
                apiError.message !== undefined && (
                  <Typography
                    component="p"
                    fontSize="13px"
                    color={'error.main'}
                    textAlign="center"
                    marginTop="10px"
                  >
                    {apiError.message}
                  </Typography>
                )}

              <Button
                type="submit"
                fullWidth
                disabled={
                  (formikLoginOtp.values.otp || '').length !== 6 ||
                  isOtpRateLimitError(apiError?.message)
                }
                sx={{
                  padding: {xxs: '12px', md: 'clamp(8px, 1.4vh, 12px)'},
                  backgroundColor: theme.palette.primary.main,
                  fontSize: {xxs: '20px', md: 'clamp(16px, 2.2vh, 20px)'},
                  fontStyle: 'Nunito sans',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  mt: 2,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main,
                    boxShadow: '0px 6px 18px rgba(233, 99, 12, 0.19)',
                  },
                  '&.Mui-disabled': {
                    color: 'white',
                    background: 'rgb(249, 183, 163)',
                    cursor: 'not-allowed',
                  },
                }}
              >
                {loading ? (
                  <Loader
                    size={35}
                    sx={{
                      color: '#ffffff !important',
                    }}
                  />
                ) : otpAccountExists ? (
                  'Verify & Login'
                ) : otpPhoneForRegistration ? (
                  'Verify & Register'
                ) : isSignIn ? (
                  'Verify & Login'
                ) : (
                  'Verify & Register'
                )}
              </Button>
            </>
          )}
        </Box>
      );
    }

    if (isSignIn) {
      return (
        <Box
          component={'form'}
          noValidate
          onSubmit={event => {
            event.preventDefault();
            deferTask(() => {
              formik.handleSubmit();
            });
          }}
        >
          <Typography
            mb={'-10px'}
            fontWeight={700}
            color={'#000'}
            fontSize={'14px'}
            fontFamily={'Nunito Sans'}
            sx={{
              opacity: 0.8,
            }}
          >
            Email Address
          </Typography>
          <CustomTextField
            fullWidth
            type="email"
            margin="normal"
            placeholder="Enter your registered email"
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onFocus={() => {
              signInEmailFocused.current = true;
              trackSignIn('focus-in', 'email', '**redacted**', 1, '');
            }}
            onBlur={async (e: any) => {
              const err = await formik.validateField('email');
              trackSignIn(
                'focus-out',
                'email',
                '**redacted**',
                err ? 0 : 1,
                err ? String(err) : ''
              );
            }}
          />
          {formik.touched.email && Boolean(formik.errors.email) && (
            <FormHelperText>{formik.errors.email}</FormHelperText>
          )}
          <Typography
            mt={{xxs: '15px', md: 'clamp(8px, 1.8vh, 25px)'}}
            mb={'-10px'}
            fontWeight={700}
            color={'#000'}
            fontSize={'14px'}
            fontFamily={'Nunito Sans'}
            sx={{
              opacity: 0.8,
            }}
          >
            Password
          </Typography>
          <CustomTextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            margin="normal"
            placeholder="Enter your password"
            name="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onFocus={() => {
              signInPasswordFocused.current = true;
              trackSignIn('focus-in', 'password', '**redacted**', 1, '');
            }}
            onBlur={async (e: any) => {
              const err = await formik.validateField('password');
              trackSignIn(
                'focus-out',
                'password',
                '**redacted**',
                err ? 0 : 1,
                err ? String(err) : ''
              );
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      deferTask(() => setShowPassword(prev => !prev));
                    }}
                    edge="end"
                    aria-label="toggle password visibility"
                    disableRipple
                    disableFocusRipple
                    sx={{
                      backgroundColor: 'transparent',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                      '&:active': {
                        backgroundColor: 'transparent',
                      },
                      '&:focus': {
                        backgroundColor: 'transparent',
                      },
                      paddingRight: '15px',
                    }}
                  >
                    {showPassword ? (
                      <Image
                        src={UnlockHirist}
                        alt="unlock-icon"
                        height={16}
                        width={12}
                      />
                    ) : (
                      <Image
                        src={LockHirist}
                        alt="lock-icon"
                        height={16}
                        width={12}
                      />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {formik.touched.password && Boolean(formik.errors.password) && (
            <FormHelperText>{formik.errors.password}</FormHelperText>
          )}
          <Box sx={{textAlign: 'right'}}>
            <Typography
              component="a"
              sx={{
                textDecoration: 'none',
                cursor: 'pointer',
                display: 'inline-block',
                fontSize: openRegisterDialog ? '14px' : '16px',
                marginTop: openRegisterDialog ? '0px' : {xxs: '8px', md: '0px'},
                fontWeight: openRegisterDialog ? 700 : 400,
                color: '#0057ff',
                textAlign: 'right',
                fontFamily: 'Nunito Sans',
              }}
              onClick={() => {
                deferTask(() => {
                  setApiError({});
                  setForgotPassword(true);
                  setEnableReCaptcha(false);
                  setIsSignIn(false);
                  handleUbaTracking('forgotPassView', 'view');
                });
              }}
            >
              Forgot Password?
            </Typography>
          </Box>
          {enableReCaptcha && (
            <Box display="flex" justifyContent="flex-start" mt={2}>
              <ReCAPTCHA
                ref={captchaRef}
                sitekey={`${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}`}
                size="normal"
                onChange={handleReCAPTCHA}
              />
            </Box>
          )}
          {apiError &&
            apiError.message !== '' &&
            apiError.message !== undefined && (
              <Typography
                component="p"
                fontSize="13px"
                color={'error.main'}
                display="inline-flex"
                flexDirection="column"
                textAlign="center"
                marginTop="10px"
              >
                {apiError.message}
                {apiError?.code === 10999 && (
                  <Box>
                    <Link
                      sx={{
                        textDecoration: 'underline',
                        textDecorationColor: theme.palette.primary.main,
                        '&:hover': {
                          textDecorationColor: theme.palette.primary.main,
                        },
                        textAlign: 'left',
                      }}
                      href={`reactivate-account${
                        apiError?.activationToken
                          ? `?em=${btoa(
                              formik.values.email
                            )}&token=${encodeURIComponent(
                              apiError?.activationToken
                            )}`
                          : ''
                      }`}
                    >
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 500,
                          lineHeight: '16px',
                          color: 'primary.main',
                          marginTop: 0.5,
                        }}
                      >
                        Reactivate your account
                      </Typography>
                    </Link>
                  </Box>
                )}
              </Typography>
            )}

          <Button
            type="submit"
            fullWidth
            // disabled={loading}
            sx={{
              padding: {xxs: '12px', md: 'clamp(8px, 1.4vh, 12px)'},
              backgroundColor: theme.palette.primary.main,
              fontSize: {xxs: '20px', md: 'clamp(16px, 2.2vh, 20px)'},
              fontStyle: 'Nunito sans',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              mt: {xxs: '30px', md: 'clamp(12px, 2.5vh, 44px)'},
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
                boxShadow: '0px 6px 18px rgba(233, 99, 12, 0.19)',
              },
            }}
          >
            {loading ? (
              <Loader
                size={35}
                sx={{
                  color: '#ffffff !important',
                }}
              />
            ) : (
              <>
                Login
                <Image
                  src={RightArrow}
                  alt="right-arrow"
                  width={16}
                  height={16}
                  style={{
                    marginLeft: '6px',
                  }}
                />
              </>
            )}
          </Button>
        </Box>
      );
    }

    return (
      <Box
        component={'form'}
        noValidate
        onSubmit={event => {
          event.preventDefault();
          deferTask(() => {
            formikSignUp.handleSubmit();
          });
        }}
      >
        <Typography
          mb={'-10px'}
          fontWeight={700}
          color={'#000'}
          fontSize={'14px'}
          sx={{
            opacity: 0.8,
          }}
        >
          Email Address
        </Typography>
        <CustomTextField
          fullWidth
          type="email"
          margin="normal"
          placeholder="you@example.com"
          name="email"
          value={formikSignUp.values.email}
          onChange={formikSignUp.handleChange}
          onFocus={() => {
            signUpEmailFocused.current = true;
            trackSignUp('focus-in', 'email', '**redacted**', 1, '');
          }}
          onBlur={async (e: any) => {
            const err = await formikSignUp.validateField('email');
            trackSignUp(
              'focus-out',
              'email',
              '**redacted**',
              err ? 0 : 1,
              err ? String(err) : ''
            );
          }}
        />
        {formikSignUp.touched.email && Boolean(formikSignUp.errors.email) && (
          <FormHelperText>{formikSignUp.errors.email}</FormHelperText>
        )}
        <Typography
          mt={{xxs: '15px', md: '20px'}}
          mb={'-10px'}
          fontWeight={700}
          color={'#000'}
          fontSize={'14px'}
          sx={{
            opacity: 0.8,
          }}
        >
          Password
        </Typography>
        <ClickAwayListener onClickAway={handleTooltipClose}>
          <div>
            <Tooltip
              PopperProps={{disablePortal: true}}
              onClose={handleTooltipClose}
              open={open}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              title={<PasswordValidationComponent />}
              arrow
              placement="top-start"
              sx={{
                '& .MuiTooltip-tooltip': {
                  backgroundColor: '#fafafa',
                  boxShadow: '0 1px 4px 0 hsla(0, 0%, 83.9%, 0.5)',
                  padding: '0px',
                },
              }}
            >
              <Box onClick={handleTooltipOpen}>
                <CustomTextField
                  fullWidth
                  type={showSignUpPassword ? 'text' : 'password'}
                  margin="normal"
                  placeholder="Enter 6 characters or more"
                  name="password"
                  value={formikSignUp.values.password}
                  onChange={formikSignUp.handleChange}
                  onFocus={() => {
                    signUpPasswordFocused.current = true;
                    trackSignUp('focus-in', 'password', '**redacted**', 1, '');
                  }}
                  onBlur={async (e: any) => {
                    const err = await formikSignUp.validateField('password');
                    trackSignUp(
                      'focus-out',
                      'password',
                      '**redacted**',
                      err ? 0 : 1,
                      err ? String(err) : ''
                    );
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            deferTask(() =>
                              setShowSignupPassword(prev => !prev)
                            );
                          }}
                          edge="end"
                          aria-label="toggle password visibility"
                          disableRipple
                          disableFocusRipple
                          sx={{
                            backgroundColor: 'transparent',
                            '&:hover': {backgroundColor: 'transparent'},
                            '&:active': {backgroundColor: 'transparent'},
                            '&:focus': {backgroundColor: 'transparent'},
                            paddingRight: '15px',
                          }}
                        >
                          {showSignUpPassword ? (
                            <Image
                              src={UnlockHirist}
                              alt="unlock-icon"
                              height={16}
                              width={12}
                            />
                          ) : (
                            <Image
                              src={LockHirist}
                              alt="lock-icon"
                              height={16}
                              width={12}
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Tooltip>
          </div>
        </ClickAwayListener>
        {formikSignUp.touched.password &&
          Boolean(formikSignUp.errors.password) && (
            <FormHelperText>{formikSignUp.errors.password}</FormHelperText>
          )}
        {/* <Box mt={'22px'} mb={2}>
          <FileUpload
            Title={
              <>
                Attach Resume{' '}
                <span style={{fontWeight: 'normal', opacity: '50%'}}>
                  (Optional)
                </span>
              </>
            }
            SubTitle={
              'Use a PDF, doc, or docx file – make sure it’s 2MB or less'
            }
            SelectText={
              window.innerWidth <= 480
                ? 'Upload resume'
                : 'Upload or drag and drop'
            }
            FileSizeLimit={2}
            FileLimitError={
              'Resume needs to be less than 2 MB & should be in PDF, doc or docx format'
            }
            FileReceiver={file => {
              setResumeFile(file);
            }}
            resumeNameLimit={17}
          />
        </Box> */}
        {apiError &&
          apiError.message !== '' &&
          apiError.message !== undefined && (
            <Typography
              component="p"
              fontSize="13px"
              color={'error.main'}
              display="inline-flex"
              textAlign="center"
              marginTop="10px"
            >
              {apiError.message}
            </Typography>
          )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          // disabled={loading}
          sx={{
            padding: '12px',
            mt: 2,
            backgroundColor: theme.palette.primary.main,
            fontSize: '20px',
            fontStyle: 'Nunito Sans',
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
              boxShadow: '0px 6px 18px rgba(233, 99, 12, 0.19)',
            },
          }}
        >
          {loading ? (
            <Loader
              size={35}
              sx={{
                color: '#ffffff !important',
              }}
            />
          ) : openRegisterDialog ? (
            <>
              Register
              <Image
                src={RightArrow}
                alt="right-arrow"
                width={16}
                height={16}
                style={{
                  marginLeft: '6px',
                }}
              />
            </>
          ) : (
            'Register'
          )}
        </Button>
      </Box>
    );
  };

  return openRegisterDialog ? (
    <Dialog
      open={openRegisterDialog}
      onClose={() => {
        handleUbaTracking('jsCloseRegisterBoxClick', 'click');
        setOpenRegisterDialog(false);
      }}
      variant={VersionVariants.VERSION_HIRIST_V1}
    >
      <Box
        // component={'form'}
        // noValidate
        // onSubmit={event => {
        //   event.preventDefault();
        //   formikSignUp.handleSubmit();
        // }}
        sx={{
          padding: {xxs: '5px', md: '0 40px 40px'},
          width: {xxs: 'auto', md: '550px'},
        }}
      >
        <Box
          display={'flex'}
          justifyContent={'space-between'}
          marginBottom={'21px'}
        >
          <Box>
            <Typography
              fontStyle={'Nunito Sans'}
              fontSize={'22px'}
              fontWeight={700}
            >
              {isForgotPassword ? 'Forgot your Password?' : ''}
            </Typography>
            <Typography
              fontStyle={'Nunito Sans'}
              fontSize={'18px'}
              fontWeight={300}
            >
              {isLoginOtp && otpAccountExists
                ? 'Sign in to Hirist'
                : isLoginOtp && needsEmail
                ? 'Register here to access premium technology jobs matching with your profile.'
                : isLoginOtp && otpPhoneForRegistration
                ? 'Register here to access premium technology jobs matching with your profile.'
                : isSignIn
                ? 'Sign in to Hirist'
                : isForgotPassword
                ? 'We’ll help you reset it and get back on track.'
                : 'Register here to access premium technology jobs matching with your profile.'}
            </Typography>
          </Box>
        </Box>
        <Box>
          {renderForm()}
          {appleErrorMessage}
          {!isForgotPassword && (
            <Typography
              mt={{xxs: 2.5, md: 2}}
              textAlign="center"
              color="#7e7e7e"
              fontSize={'16px'}
              fontFamily={'Nunito Sans'}
            >
              {isSignIn ? 'Don’t have an account?' : 'Already have an account?'}{' '}
              <Typography
                component="a"
                sx={{
                  textDecoration: 'none',
                  fontWeight: '700',
                  cursor: 'pointer',
                  color: theme.palette.primary.main,
                }}
                onClick={toggleAuthMode}
              >
                {isSignIn ? 'Sign Up' : 'Sign In'}
              </Typography>
            </Typography>
          )}
          {!isForgotPassword && isEmailOtpEnabled && (
            <Typography
              mt={1}
              textAlign="center"
              fontSize={'16px'}
              fontFamily={'Nunito Sans'}
            >
              <Typography
                component="a"
                sx={{
                  textDecoration: 'none',
                  fontWeight: '700',
                  cursor: 'pointer',
                  color: theme.palette.primary.main,
                }}
                data-testid="auth-otp-toggle-link-compact"
                onClick={toggleLoginOtp}
              >
                {isSignIn
                  ? isLoginOtp
                    ? 'Use Password to Login'
                    : 'Use OTP to Login'
                  : isLoginOtp
                  ? 'Register with password'
                  : 'Register with OTP'}
              </Typography>
            </Typography>
          )}
          <Box
            sx={{
              pb: 1,
              '& > hr': {
                mt: '16px !important',
              },
            }}
          >
            <GoogleLogin />
            <AppleLogin
              onAccountNotFound={() =>
                setAppleError(
                  'It looks like you haven’t signed up with Apple yet. Please create your account using an iPhone or iPad to continue.'
                )
              }
              onError={msg => setAppleError(msg)}
            />
          </Box>
        </Box>
      </Box>
    </Dialog>
  ) : (
    <MuiDialog open={props?.openDialog} fullScreen data-testid="auth-dialog">
      <Box
        display="flex"
        sx={{
          alignItems: {xxs: 'center', md: 'stretch'},
          minHeight: {md: '100vh'},
        }}
      >
        <Grid
          container
          direction={{xxs: 'column-reverse', md: 'row'}}
          sx={{minHeight: {md: '100vh'}, backgroundColor: {md: 'white'}}}
        >
          <Grid item xxs={12} md={4}>
            <Box>
              <AuthLeft>
                <Image
                  src={HiristBackground}
                  alt="Hirist Background"
                  layout="fill"
                />

                <SiteLogo>
                  <Image src={HiristTextLogoWhiteNew} alt="Logo" height={30} />
                </SiteLogo>

                <PeopleSaying>
                  Choose a job you love, and you never have to work a day in
                  your life
                  <Typography component="p" marginTop={'16px'}>
                    - Confucius
                  </Typography>
                </PeopleSaying>

                <CompanyList>
                  <Typography
                    display={'flex'}
                    justifyContent={'center'}
                    marginTop={'16px'}
                    fontSize={'16px'}
                    fontFamily={'Nunito Sans'}
                  >
                    Companies you might love to work with
                  </Typography>
                  <Box
                    display="flex"
                    justifyContent="space-evenly"
                    mt={1}
                    width="100%"
                    data-testid="hirist-companies-list"
                  >
                    {companies.map((company: any, index: any) => (
                      <Box
                        key={index}
                        width="20%"
                        display={
                          index === 3 ? {xxs: 'none', md: 'flex'} : 'flex'
                        }
                        sx={{
                          backgroundImage: `url("${company.imageUrl}")`,
                          backgroundSize: 'contain',
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'center',
                          aspectRatio: '16/9',
                          cursor: 'pointer',
                        }}
                        data-testid={`hirist-company-item-${index}`}
                        onClick={() => {
                          deferTask(() => {
                            const {refText} = getRefTagAndOrigin();
                            handleTracking('jsClickLoginLogo', {
                              event_category: 'jsHomePage',
                              Source: isSignIn ? 'login' : 'register',
                              Ref: `${refText}`,
                              CompanyName: company.title,
                              Device:
                                sessionStorage.getItem('device') || 'desktop',
                              Device_Type:
                                sessionStorage.getItem('deviceType') || 'web',
                            });
                            handleUbaTracking(
                              'jsLoginLogoClick',
                              'click',
                              isSignIn ? 'signin' : 'signup'
                            );
                            openUrl(`${company.link}`);
                          });
                        }}
                      />
                    ))}
                  </Box>
                </CompanyList>
              </AuthLeft>
            </Box>
          </Grid>
          <Grid
            item
            xxs={12}
            md={8}
            sx={{
              display: {md: 'flex'},
              flexDirection: {md: 'column'},
              maxHeight: {md: '100vh'},
            }}
          >
            <Box
              display={'flex'}
              justifyContent={isForgotPassword ? 'space-between' : 'flex-end'}
              marginTop={{xxs: '20px', md: '15px'}}
              marginRight={{xxs: '35px', md: '46px'}}
            >
              <Box display={isForgotPassword ? 'flex' : 'none'}>
                <Box
                  onClick={handlebackclick}
                  sx={{
                    cursor: 'pointer',
                    position: 'relative',
                    width: '50px',
                    height: '50px',
                    backgroundColor: '#f2f5fa',
                    borderRadius: '50%',
                    marginLeft: {xxs: '30px', md: '55px'},
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      backgroundColor: 'white',
                      boxShadow: '0 6px 14px 0 rgba(0, 0, 0, 0.09)',
                    },
                  }}
                >
                  <Image
                    src={ChevronRight}
                    alt="left-arrow"
                    height={20}
                    width={20}
                    style={{transform: 'rotate(180deg)'}}
                  />
                </Box>
              </Box>
              <Box display={'flex'}>
                <Button
                  onClick={handleDownloadClick}
                  sx={{
                    height: '48px',
                    borderRadius: '24px',
                    border: '1px solid #fceecf',
                    backgroundColor: '#fffbf1',
                    padding: '11px 12px 11px 14px',
                    marginRight: '30px',
                    display: {xxs: 'none', md: 'flex'},
                    alignItems: 'center',
                    '&:hover': {
                      backgroundColor: '#fffbf1',
                    },
                  }}
                >
                  <Image
                    src={PhoneIcon}
                    alt="phoneIcon"
                    height={24}
                    width={24}
                  />
                  <Typography
                    color={theme.palette.primary.main}
                    marginLeft={1.5}
                    fontSize={'15px'}
                    fontFamily={'Nunito Sans'}
                    fontWeight={400}
                  >
                    Download App
                    <Image
                      src={OrangeRightArrow}
                      alt="orange-arrow"
                      height={13}
                      width={13}
                      style={{
                        marginLeft: '12px',
                        paddingTop: '3px',
                      }}
                    />
                  </Typography>
                </Button>
                <Box
                  onClick={() => {
                    setLoading(true);
                    deferTask(() => {
                      const action = isSignIn
                        ? 'signInCancel'
                        : isForgotPassword
                        ? ''
                        : 'signUpCancel';
                      handleTracking(action, {
                        event_category: 'Offline Landing page',
                        event_label: 'cross=1',
                      });
                      handleUbaTracking('jsCloseRegisterBoxClick', 'click');
                      props.setOpenDialog(false);
                      formik.setErrors({});
                      formikForgot.setErrors({});
                      formikSignUp.setErrors({});
                      formikLoginOtp.setErrors({});
                      setApiError({});
                      setIsLoginOtp(true);
                      setOtpSent(false);
                      setOtpTimer(0);
                      setOtpIdentifier('');
                      setOtpIdentifierError('');
                      setAwaitingRegistrationEmail(false);
                      setOtpPhoneForRegistration('');
                      setOtpAccountExists(false);
                      isOpenDialog(false);
                      setLoading(false);
                    });
                  }}
                  sx={{
                    cursor: 'pointer',
                    position: 'relative',
                    width: '50px',
                    height: '50px',
                    backgroundColor: '#f2f5fa',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      backgroundColor: 'white',
                      boxShadow: '0 6px 14px 0 rgba(0, 0, 0, 0.09)',
                    },
                  }}
                >
                  <Image
                    src={CrossIcon}
                    alt="cross-icon"
                    height={14}
                    width={14}
                  />
                </Box>
              </Box>
            </Box>
            <Box
              display="flex"
              flexDirection={{xxs: 'column', sm: 'row'}}
              justifyContent="center"
              marginTop={0}
              height={
                isSignIn
                  ? {xxs: '1%', md: 'auto'}
                  : isForgotPassword
                  ? {xxs: '6%', md: 'auto'}
                  : {xxs: '1%', md: 'auto'}
              }
              sx={{
                backgroundColor: 'white',
                flex: {md: 1},
                alignItems: {md: 'center'},
              }}
            >
              <Box
                width={{xxs: '90%', md: '70%'}}
                px={3}
                marginBottom={'1vh'}
                alignSelf={{xxs: 'center', sm: 'start'}}
              >
                <Typography
                  fontSize={{xxs: '20px', md: '26px'}}
                  fontFamily={'Nunito Sans'}
                  fontWeight={700}
                >
                  {isForgotPassword ? 'Forgot your Password?' : ''}
                </Typography>
                <Typography
                  fontSize={
                    isForgotPassword
                      ? '21px'
                      : {xxs: '32px', md: 'clamp(22px, 4vh, 46px)'}
                  }
                  fontWeight={isForgotPassword ? 200 : 500}
                  fontFamily={'Nunito Sans'}
                  marginTop={
                    isForgotPassword
                      ? {xxs: '5px', md: '20px'}
                      : {xxs: '-10px', md: 'clamp(-10px, -1vh, -4px)'}
                  }
                  sx={{
                    lineHeight: {md: 1.1},
                  }}
                >
                  {isForgotPassword
                    ? 'We’ll help you reset it and get back on track.'
                    : isLoginOtp && otpAccountExists
                    ? 'Login here'
                    : isLoginOtp && needsEmail
                    ? 'Register here'
                    : isLoginOtp && otpPhoneForRegistration
                    ? 'Register here'
                    : isSignIn
                    ? 'Login here'
                    : 'Register here'}
                </Typography>
                <Box sx={{mt: {xxs: 2.5, md: 'clamp(8px, 2vh, 24px)'}}}>
                  {renderForm()}
                </Box>
                {appleErrorMessage}
                {!isForgotPassword && (
                  <Typography
                    mt={{xxs: 2.5, md: 'clamp(8px, 1.5vh, 16px)'}}
                    textAlign="center"
                    color="#7e7e7e"
                    fontSize={'16px'}
                    fontFamily={'Nunito Sans'}
                  >
                    {isSignIn
                      ? 'Don’t have an account?'
                      : 'Already have an account?'}{' '}
                    <Typography
                      component="a"
                      sx={{
                        textDecoration: 'none',
                        fontWeight: '700',
                        cursor: 'pointer',
                        color: theme.palette.primary.main,
                      }}
                      data-testid="auth-toggle-link"
                      onClick={toggleAuthMode}
                    >
                      {isSignIn ? 'Register now!' : 'Login now!'}
                    </Typography>
                  </Typography>
                )}
                {!isForgotPassword && isEmailOtpEnabled && (
                  <Typography
                    mt={4}
                    textAlign="center"
                    fontSize={'16px'}
                    fontFamily={'Nunito Sans'}
                  >
                    <Typography
                      component="a"
                      sx={{
                        textDecoration: 'none',
                        fontWeight: '700',
                        cursor: 'pointer',
                        color: theme.palette.primary.main,
                        '&:hover': {
                          textDecoration: 'underline',
                          textDecorationColor: 'currentColor',
                        },
                      }}
                      data-testid="auth-otp-toggle-link"
                      onClick={toggleLoginOtp}
                    >
                      {isSignIn
                        ? isLoginOtp
                          ? 'Use Password to Login'
                          : 'Use OTP to Login'
                        : isLoginOtp
                        ? 'Register with password'
                        : 'Register with OTP'}
                    </Typography>
                  </Typography>
                )}
                <Box
                  sx={{
                    pb: {md: 'clamp(12px, 2vh, 32px)'},
                    '& > hr': {
                      mt: {md: 'clamp(8px, 1.5vh, 24px) !important'},
                    },
                  }}
                >
                  <GoogleLogin />
                  <AppleLogin
                    onAccountNotFound={() =>
                      setAppleError(
                        'It looks like you haven’t signed up with Apple yet. Please create your account using an iPhone or iPad to continue.'
                      )
                    }
                    onError={msg => setAppleError(msg)}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  padding: '10px',
                  marginBottom: '50px',
                  display: {xxs: 'block', sm: 'none'},
                }}
              >
                <DownloadAppOnJobdetail showWidgetMobile={true} />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </MuiDialog>
  );
};

export default HiristLoginForm;
